import { escapePropertyName, kebabCase } from './utils';

export interface TokenOptions {
  /**
   * If true, will use the token's value as a fallback value when creating a CSS
   * variable reference.
   */
  fallback?: boolean;
  /**
   * A function that maps a genrated CSS variable reference to a CSS property.
   *
   * @example
   * toResolvedRef({ mapReference: (reference) => `rgb(${reference} / <alpha-value>)` })
   */
  mapReference?: (value: string) => string;
}
export class Token<
  T extends
    | string
    | Token
    | Collection
    // biome-ignore lint/suspicious/noExplicitAny: legacy lint
    | Array<string | Token | Collection> = any,
> {
  constructor(
    readonly value: T,
    readonly name: string,
  ) {}

  private static readonly _encounters = new Set();

  getPropertyName() {
    return `--${escapePropertyName(this.name)}`;
  }

  toPropertyValue(options?: TokenOptions): string {
    if (Token._encounters.has(this)) {
      throw new Error(`Token ${this.name} is self-referencing`);
    }

    Token._encounters.add(this);

    if (typeof this.value === 'string') {
      Token._encounters.clear();

      return this.value;
    }

    if (Array.isArray(this.value)) {
      const value = this.value
        .map((tokenOrLiteral) => {
          if (typeof tokenOrLiteral === 'string') {
            return tokenOrLiteral;
          }

          return tokenOrLiteral.toResolvedRef(options);
        })
        .join('');

      Token._encounters.clear();

      return value;
    }

    const value = this.value.toPropertyValue(options);

    Token._encounters.clear();

    return value;
  }

  toResolvedRef(options?: TokenOptions): string {
    if (typeof this.value === 'string') {
      return this.toPropertyValue(options);
    }

    if (Array.isArray(this.value)) {
      return this.value
        .map((tokenOrLiteral) => {
          if (typeof tokenOrLiteral === 'string') {
            return tokenOrLiteral;
          }

          return tokenOrLiteral.toResolvedRef(options);
        })
        .join('');
    }

    // biome-ignore lint/suspicious/noImplicitAnyLet: legacy lint
    let result;

    if (options?.fallback) {
      result = `var(${this.value.getPropertyName()}, ${this.toPropertyValue(options)})`;
    } else {
      result = `var(${this.value.getPropertyName()})`;
    }

    if (options?.mapReference) {
      result = options.mapReference(result);
    }

    return result;
  }

  getPropertyDeclarations(options?: TokenOptions): Record<string, string> {
    return {
      [escapePropertyName(this.name)]: this.toResolvedRef(options),
    };
  }

  toPropertyRef(options?: TokenOptions): string {
    // biome-ignore lint/suspicious/noImplicitAnyLet: legacy lint
    let result;

    if (options?.fallback) {
      result = `var(${this.getPropertyName()}, ${this.toPropertyValue(options)})`;
    } else {
      result = `var(${this.getPropertyName()})`;
    }

    if (options?.mapReference) {
      result = options.mapReference(result);
    }

    return result;
  }
}

type KeyValuePair = Record<string, string>;
interface RecursiveKeyValuePair {
  [key: string]: string | RecursiveKeyValuePair;
}

export class Collection<
  // biome-ignore lint/suspicious/noExplicitAny: legacy lint
  T extends Record<string | symbol | number, Token | Collection<any>> = Record<
    string | symbol | number,
    // biome-ignore lint/suspicious/noExplicitAny: legacy lint
    Token | Collection<any>
  >,
> {
  static readonly Default: unique symbol = Symbol('DEFAULT');

  // TODO: Token modes (e.g. themes)
  constructor(readonly tokens: T) {
    this.tokens = tokens;
  }

  get<K extends keyof T>(key: K): T[K] {
    return this.tokens[key] as T[K];
  }

  getDefault(): Token | undefined {
    if (Collection.Default in this.tokens) {
      const tokenOrCollection = this.tokens[Collection.Default];

      if (tokenOrCollection instanceof Token) {
        return tokenOrCollection;
      }

      if (tokenOrCollection) {
        return tokenOrCollection.getDefault();
      }
    }

    return undefined;
  }

  *keys() {
    for (const key of Object.getOwnPropertySymbols(this.tokens)) {
      yield key as keyof T;
    }

    for (const key of Object.getOwnPropertyNames(this.tokens)) {
      yield key as keyof T;
    }
  }

  *entries(): Generator<[keyof T, T[keyof T]], void, unknown> {
    for (const key of this.keys()) {
      yield [key, this.get(key)];
    }
  }

  *values(): Generator<T[keyof T], void, unknown> {
    for (const key of this.keys()) {
      yield this.get(key);
    }
  }

  toStyle(): string {
    return Object.entries(this.tokens)
      .map(([_key, tokenOrCollection]) => {
        if (tokenOrCollection instanceof Collection) {
          return tokenOrCollection.toStyle();
        }

        return `${tokenOrCollection.getPropertyName()}: ${tokenOrCollection.toPropertyValue()};`;
      })
      .join('\n');
  }

  /**
   * Get the property name of the default token in the collection.
   */
  getPropertyName(): string {
    return this.getDefault()?.getPropertyName() ?? '';
  }

  getPropertyDeclarations(options?: TokenOptions): Record<string, string> {
    const vars: Record<string, string> = {};

    for (const tokenOrCollection of this.values()) {
      Object.assign(vars, tokenOrCollection.getPropertyDeclarations(options));
    }

    return vars;
  }

  toTailwindConfig(options?: TokenOptions) {
    const vars: RecursiveKeyValuePair = {};

    for (const [key, tokenOrCollection] of this.entries()) {
      const kebabKey =
        key === Collection.Default
          ? 'DEFAULT'
          : kebabCase(String(key).replace(/([A-Z])([A-Z])/g, '$1 $2'));

      if (tokenOrCollection instanceof Collection) {
        const collectionVars = tokenOrCollection.toTailwindConfig(options);

        vars[kebabKey] = collectionVars;
      } else {
        vars[kebabKey] = tokenOrCollection.toPropertyRef(options);
      }
    }

    return vars as Collection extends T[keyof T]
      ? RecursiveKeyValuePair
      : KeyValuePair;
  }

  toPropertyValue(options?: TokenOptions): string {
    return (
      // biome-ignore lint/suspicious/noExplicitAny: legacy lint
      (this.tokens as any)[Collection.Default]?.toPropertyValue(options) ?? ''
    );
  }

  toResolvedRef(options?: TokenOptions): string {
    return (
      // biome-ignore lint/suspicious/noExplicitAny: legacy lint
      (this.tokens as any)[Collection.Default]?.toResolvedRef(options) ?? ''
    );
  }

  toPropertyRef(options?: TokenOptions): string {
    return (
      // biome-ignore lint/suspicious/noExplicitAny: legacy lint
      (this.tokens as any)[Collection.Default]?.toPropertyRef(options) ?? ''
    );
  }
}
